import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { useHomepageImages } from "@/hooks/useHomepageImages";
import heroDefault from "@/assets/hero-beach-picnic-diverse.jpg";


const BohoHeroOptimized = () => {
  const { images: dbImages, isLoading, getImageUrl } = useHomepageImages();

  // Get active images from database ONLY
  const activeImages = useMemo(() => {
    if (!isLoading && dbImages && dbImages.length > 0) {
      const activeDbImages = dbImages
        .filter(img => img.is_active)
        .sort((a, b) => a.display_order - b.display_order);
      
      // Active hero images loaded
      return activeDbImages;
    }
    return [];
  }, [dbImages, isLoading]);

  // Select random image ONCE on component mount/load - no rotation while viewing
  const selectedImageData = useMemo(() => {
    if (activeImages.length === 0) return null;
    
    // Random selection happens only on initial load or when admin changes active images
    const randomIndex = Math.floor(Math.random() * activeImages.length);
    return activeImages[randomIndex];
  }, [activeImages]); // Only re-runs when activeImages changes, not on every render

  const selectedImageUrl = selectedImageData ? getImageUrl(selectedImageData.file_path) : null;
  const positioning = 'object-top'; // Fixed positioning to prevent head chopping

  // Aggressive preload for LCP optimization (safe for relative URLs)
  useMemo(() => {
    if (!selectedImageUrl) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Preloading single hero image

    // Only preload hero images on homepage to avoid unnecessary preloads
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      return;
    }

    // Avoid duplicate preload tags
    if (!document.querySelector(`link[rel="preload"][as="image"][href="${selectedImageUrl}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = selectedImageUrl;
      (link as any).fetchpriority = 'high';
      // Only set crossorigin for external domains, not same-origin or relative URLs
      if (selectedImageUrl.startsWith('http') && 
          !selectedImageUrl.startsWith(window.location.origin) &&
          !selectedImageUrl.startsWith('/')) {
        link.setAttribute('crossorigin', 'anonymous');
      }
      document.head.appendChild(link);
    }

    // Preconnect if image is on a different origin
    try {
      const origin = new URL(selectedImageUrl, window.location.origin).origin;
      if (origin && origin !== window.location.origin && !document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
        const preconnectLink = document.createElement('link');
        preconnectLink.rel = 'preconnect';
        preconnectLink.href = origin;
        preconnectLink.setAttribute('crossorigin', 'anonymous');
        document.head.appendChild(preconnectLink);
      }
    } catch {
      // ignore invalid urls
    }

    // Prime the cache ASAP
    const img = new Image();
    img.decoding = 'sync';
    (img as any).fetchpriority = 'high';
    img.src = selectedImageUrl;
  }, [selectedImageUrl]);

  return (
    <section className="relative h-[70vh] md:h-[60vh] overflow-hidden bg-gradient-earth">
      {/* Direct image loading for LCP optimization */}
      <div className="absolute inset-0">
        {selectedImageUrl ? (
          <img
            src={selectedImageUrl}
            alt="Bohemian lifestyle background"
            className={`w-full h-full object-cover ${positioning} transition-opacity duration-300 opacity-100`}
            loading="eager"
            {...({ fetchpriority: "high" } as any)}
            decoding="sync"
            crossOrigin={selectedImageUrl.startsWith('http') && !selectedImageUrl.startsWith('/') ? 'anonymous' : undefined}
            width="1920"
            height="1080"
            onError={(e) => {
              e.currentTarget.src = heroDefault;
            }}
          />
        ) : (
          <img
            src={heroDefault}
            alt="Bohemian lifestyle background (fallback)"
            className="w-full h-full object-cover object-top transition-opacity duration-300 opacity-100"
            loading="eager"
            {...({ fetchpriority: "high" } as any)}
            decoding="sync"
            width="1920"
            height="1080"
          />
        )}
      </div>
      
      <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-center">
        <div className="max-w-2xl text-center animate-fade-in">
          <div className="flex justify-center mb-6">
            <img 
              src="/lovable-uploads/bohostyleco-logo-1000x600-bl.png" 
              alt="Boho Style Co." 
              className="h-40 md:h-48 w-auto filter brightness-0 invert drop-shadow-lg"
            />
          </div>
          <p className="text-lg text-white mb-8 leading-relaxed drop-shadow-sm">
            Embrace your authentic self with our curated collections of bohemian-inspired clothing, home and lifestyle essentials.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/collections">
              <Button 
                variant="default" 
                size="lg" 
                className="px-8 py-6 text-lg font-medium tracking-wide"
              >
                Explore Collections
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BohoHeroOptimized;